import { forwardRef } from 'react';
import { cn } from '@/shared/utils/cn';
import { Icon } from '@/modules/Icon';
import type { AnyIconDefinition } from '@/shared/types/types';
import { useTabs } from '../../hooks/useTabs';
import type { TabsProps, TabItem, TabSize } from '../../types';

const SIZE_CLASSES_INACTIVE: Record<TabSize, string> = {
  lg: 'ui-px-24 ui-py-12',
  md: 'ui-px-16 ui-py-8',
  sm: 'ui-px-12 ui-py-6',
  xs: 'ui-px-12 ui-py-4',
  xxl: 'ui-px-8 ui-py-4',
};

const SIZE_CLASSES_ACTIVE: Record<TabSize, string> = {
  lg: 'ui-px-24 ui-py-16',
  md: 'ui-px-16 ui-py-12',
  sm: 'ui-px-12 ui-py-10',
  xs: 'ui-px-12 ui-py-8',
  xxl: 'ui-px-8 ui-py-8',
};

const FONT_BASE: Record<TabSize, string> = {
  lg: 'ui-text-body-sm-regular ui-leading-body-sm-regular',
  md: 'ui-text-body-sm-regular ui-leading-body-sm-regular',
  sm: 'ui-text-body-sm-regular ui-leading-body-sm-regular',
  xs: 'ui-text-body-sm-regular ui-leading-body-sm-regular',
  xxl: 'ui-text-body-sm-regular ui-leading-body-sm-regular',
};

const FONT_WEIGHT: Record<string, string> = {
  regular: 'ui-font-body-sm-regular',
  bold: 'ui-font-body-sm-bold',
};

const ICON_SIZE: Record<TabSize, 'xs' | 'sm'> = {
  lg: 'sm',
  md: 'sm',
  sm: 'sm',
  xs: 'sm',
  xxl: 'xs',
};

/**
 * Tabs — navigation component with size and pill variants.
 *
 * Renders a tablist with selectable tabs, optional icons, optional add button,
 * and accessible keyboard navigation (ArrowLeft/Right, Home, End).
 *
 * @example
 * ```tsx
 * <Tabs
 *   items={[
 *     { id: 'design', label: 'Design', icon: faShapes },
 *     { id: 'code', label: 'Code', icon: faCode },
 *     { id: 'preview', label: 'Preview' },
 *   ]}
 *   appearance={{ size: 'md', pill: true }}
 *   selection={{ activeTab: current, onTabChange: setCurrent }}
 *   addButton={{ show: true, onClick: () => addNewTab() }}
 * />
 * ```
 */
export const Tabs = forwardRef<HTMLDivElement, TabsProps>(
  ({ items, className, appearance, selection, addButton }, ref) => {
    const { size = 'md', pill = true } = appearance ?? {};
    const { activeTab, defaultActiveTab, onTabChange } = selection ?? {};
    const { show: showAdd = true, onClick: onAddClick } = addButton ?? {};

    const { currentTab, selectTab, handleKeyDown, tabRefs } = useTabs({
      items,
      activeTab,
      defaultActiveTab,
      onTabChange,
    });

    const renderTab = (item: TabItem) => {
      const isActive = currentTab === item.id;

      const tabClasses = cn(
        'ui-imj',
        'ui-inline-flex ui-items-center ui-gap-4',
        'ui-cursor-pointer ui-select-none ui-border-solid',
        'ui-transition-colors ui-duration-150 ui-ease-in-out',
        'ui-tracking-[0.5px]',
        'focus-visible:ui-outline-none focus-visible:ui-ring-2 focus-visible:ui-ring-fill-brand focus-visible:ui-ring-offset-1',
        isActive ? SIZE_CLASSES_ACTIVE[size] : SIZE_CLASSES_INACTIVE[size],
        FONT_BASE[size],
        isActive ? FONT_WEIGHT.bold : FONT_WEIGHT.regular,
        pill && 'ui-rounded-t-control-xs',
        'ui-border-solid ui-border-t ui-border-l ui-border-r ui-border-b-0',
        isActive && ['ui-bg-fill-brand ui-text-on-color ui-border-transparent'],
        !isActive && [
          'ui-bg-surface-default ui-text-primary',
          'ui-border-DEFAULT',
          'hover:ui-bg-fill-hover',
        ],
        item.disabled && 'ui-opacity-50 ui-cursor-not-allowed'
      );

      return (
        <button
          key={item.id}
          ref={(el) => {
            if (el) {
              tabRefs.current.set(item.id, el);
            } else {
              tabRefs.current.delete(item.id);
            }
          }}
          role="tab"
          type="button"
          id={`tab-${item.id}`}
          aria-selected={isActive}
          aria-controls={`tabpanel-${item.id}`}
          aria-disabled={item.disabled}
          tabIndex={isActive ? 0 : -1}
          className={tabClasses}
          onClick={() => selectTab(item.id)}
          onKeyDown={(e) => handleKeyDown(e, item.id)}
          disabled={item.disabled}
        >
          {item.icon && (
            <Icon name={item.icon} size={ICON_SIZE[size]} color={isActive ? 'white' : 'default'} />
          )}
          <span className="ui-relative ui-inline-flex ui-items-center ui-justify-center">
            <span
              className={cn(
                'ui-pointer-events-none ui-invisible',
                FONT_BASE[size],
                FONT_WEIGHT.bold
              )}
              aria-hidden="true"
            >
              {item.label}
            </span>
            <span className="ui-absolute ui-inset-0 ui-flex ui-items-center ui-justify-center">
              {item.label}
            </span>
          </span>
          {item.badge && !pill && isActive && (
            <span
              className="ui-inline-block ui-h-12 ui-w-12 ui-rounded-control-pill ui-bg-fill-danger"
              aria-label="notification"
            />
          )}
        </button>
      );
    };

    const addButtonClasses = cn(
      'ui-imj',
      'ui-inline-flex ui-items-center ui-justify-center',
      'ui-cursor-pointer ui-border-0 ui-bg-transparent',
      'ui-transition-colors ui-duration-150',
      'hover:ui-bg-fill-hover ui-rounded-control-xs',
      'focus-visible:ui-outline-none focus-visible:ui-ring-2 focus-visible:ui-ring-fill-brand',
      SIZE_CLASSES_INACTIVE[size]
    );

    return (
      <div ref={ref} className={cn('ui-imj ui-flex ui-items-end ui-gap-8 ui-px-16', className)}>
        <div role="tablist" aria-label="Tabs" className="ui-flex ui-items-end">
          {items.map(renderTab)}
        </div>
        {showAdd && (
          <button
            type="button"
            aria-label="Add tab"
            className={addButtonClasses}
            onClick={onAddClick}
          >
            <Icon
              name={'plus' as unknown as AnyIconDefinition}
              size={ICON_SIZE[size]}
              color="default"
            />
          </button>
        )}
      </div>
    );
  }
);

Tabs.displayName = 'Tabs';
